import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import {useLocation, useNavigate} from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Pda } from '../components/Pda';
import { Header } from '../components/Header';
import { TmsTaskCardItem } from '../components/TmsTaskCardItem';
import { t } from '../../../i18n';
import { TmsPageState } from '../types/tms';
import { TmsLoadingScanComponent, HandleTmsLoadingScanEventResult } from './components/TmsLoadingScanComponent';
import { isTrackingNumber, isBagNumber, isTtBagNumber, showBarCodeError } from '../../../utils/BarCodeUtil';
import { parseException } from '../../../utils/exceptionUtils';
import {getBottomNavBarContainerStyle} from "../../../utils/deviceUtils";
import { Button, Dialog, Toast } from "antd-mobile";
import {PdaPath} from "../../../enums/PdaNavigatePathEnum";
import {TmsWaypointStepIndicator} from "./components/TmsWaypointStepIndicator";
import { WaypointType, TmsOperationSourceEnum } from '../../../enums/TmsEnum';
import {
    GET_HAS_TMS_LOADING_DETAIL,
    HasTmsLoadingDetailResponse,
    GET_NEEDS_TMS_UNLOADING_OPERATION,
    NeedsTmsUnloadingOperationResponse
} from '../../../graphql/queries/tmsTaskWarehouseOperationQueries';

const HANDLE_TMS_LOADING_SCAN_EVENT = gql`
  mutation HandleTmsLoadingScanEvent($input: HandleTmsLoadingScanEventInput!) {
    handleTmsLoadingScanEvent(input: $input) {
      success
      parcelQuantity
      message
      destinationSortingCode
    }
  }
`;

const PageBody = ({ taskId, taskWayPointId , routeName}: { taskId: number; taskWayPointId: number; routeName: string }) => {
  const [handleTmsLoadingScanEvent] = useMutation<{ handleTmsLoadingScanEvent: HandleTmsLoadingScanEventResult }>(HANDLE_TMS_LOADING_SCAN_EVENT);

  const handleScanEvent = async (barCode: string, forceSubmit: boolean = false): Promise<HandleTmsLoadingScanEventResult> => {
    if (!isTrackingNumber(barCode) && !isBagNumber(barCode) && !isTtBagNumber(barCode)) {
      showBarCodeError(barCode);
      return { success: false };
    }

    Toast.show({
      icon: 'loading',
      content: t('pda.common.loading') || 'Loading...',
      duration: 0,
      maskClickable: false,
    });

    try {
      const { data } = await handleTmsLoadingScanEvent({
        variables: {
          input: {
            taskId,
            taskWayPointId,
            barCode,
            forceSubmit,
            operationSource: TmsOperationSourceEnum.PDA,
          }
        }
      });
      return data?.handleTmsLoadingScanEvent || { success: false };
    } finally {
      Toast.clear();
    }
  };

  return (
      <TmsLoadingScanComponent
          value1Placeholder={t('pda.common.bagCodeTrackingNo') || 'Bag Code/Tracking No'}
          value1ColumnTitle={t('pda.common.bagPackage') || 'Bag/Package'}
          handleScanEvent={handleScanEvent}
          routeName={routeName}
      />
  );
};

export default function TmsLoadingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const client = useApolloClient();
  const {backRoute, ...taskData} = location.state as TmsPageState;
  const buttonContainerRef = useRef<HTMLDivElement>(null);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastViewportHeightRef = useRef<number>(window.visualViewport?.height || window.innerHeight);

  const taskId = taskData?.taskId ? parseInt(taskData.taskId) : NaN;
  const taskWayPointId = taskData?.taskWaypointId ? parseInt(taskData.taskWaypointId) : NaN;
  const hasUnLoadingTime = taskData?.task?.hasUnLoadingTime === true;
  const isOriginStation = taskData?.waypointType === WaypointType.ORIGIN;
  const shouldCheckUnloading =
    !isNaN(taskId) && !isNaN(taskWayPointId) && !hasUnLoadingTime && !isOriginStation;

  const { data: unloadingCheckData } = useQuery<NeedsTmsUnloadingOperationResponse>(
    GET_NEEDS_TMS_UNLOADING_OPERATION,
    {
      variables: { taskId, taskWayPointId },
      skip: !shouldCheckUnloading
    }
  );

  useEffect(() => {
    if (!shouldCheckUnloading) return;
    if (unloadingCheckData?.needsTmsUnloadingOperation === true) {
      Dialog.alert({
        content: t('pda.pages.sealed.unloadingRequired'),
        confirmText: t('pda.common.confirm') || '确认',
        onConfirm: () => {
          if (backRoute) navigate(backRoute);
          else navigate(-1);
        },
      });
    }
  }, [shouldCheckUnloading, unloadingCheckData, backRoute, navigate]);

  // 监听键盘显示/隐藏 - 直接操作 DOM，不触发 React 重新渲染
  useEffect(() => {
    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      // 只在非按钮元素获得焦点时隐藏按钮
      if (target && !target.closest('button') && buttonContainerRef.current) {
        // 清除之前的超时
        if (focusTimeoutRef.current) {
          clearTimeout(focusTimeoutRef.current);
        }
        // 延迟一小段时间，确保焦点真正停留在输入框上（避免快速切换导致的问题）
        focusTimeoutRef.current = setTimeout(() => {
          if (buttonContainerRef.current) {
            buttonContainerRef.current.style.opacity = '0';
            buttonContainerRef.current.style.pointerEvents = 'none';
          }
        }, 50);
      }
    };

    const handleFocusOut = () => {
      // 清除之前的超时
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
      if (buttonContainerRef.current) {
        // 直接显示按钮
        buttonContainerRef.current.style.opacity = '1';
        buttonContainerRef.current.style.pointerEvents = 'auto';
      }
    };

    // 监听 visualViewport 变化，检测虚拟键盘的显示/隐藏
    const handleViewportChange = () => {
      const currentHeight = window.visualViewport?.height || window.innerHeight;
      const lastHeight = lastViewportHeightRef.current;
      
      // 如果高度增加了，说明键盘消失了
      if (currentHeight > lastHeight && buttonContainerRef.current) {
        // 确保按钮显示
        buttonContainerRef.current.style.opacity = '1';
        buttonContainerRef.current.style.pointerEvents = 'auto';
      }
      // 如果高度减少了，说明键盘显示了，检查焦点
      else if (currentHeight < lastHeight) {
        const activeElement = document.activeElement as HTMLElement;
        if (activeElement && !activeElement.closest('button') && buttonContainerRef.current) {
          buttonContainerRef.current.style.opacity = '0';
          buttonContainerRef.current.style.pointerEvents = 'none';
        }
      }
      
      lastViewportHeightRef.current = currentHeight;
    };

    window.addEventListener('focusin', handleFocusIn);
    window.addEventListener('focusout', handleFocusOut);
    window.visualViewport?.addEventListener('resize', handleViewportChange);

    return () => {
      window.removeEventListener('focusin', handleFocusIn);
      window.removeEventListener('focusout', handleFocusOut);
      window.visualViewport?.removeEventListener('resize', handleViewportChange);
      if (focusTimeoutRef.current) {
        clearTimeout(focusTimeoutRef.current);
      }
    };
  }, []);

  if (!taskData) {
    return null;
  }

  const routeName = taskData.routeName;

  if (isNaN(taskId) || isNaN(taskWayPointId)) {
    return null;
  }

  const handleBack = () => {
    const route = backRoute || "/pda/home";
    navigate(route);
  };

  const handleNextStep = async () => {
    // 始发站：需要先装车（存在装载明细）才能进入下一步
    if (taskData.waypointType === WaypointType.ORIGIN) {
      try {
        const { data } = await client.query<HasTmsLoadingDetailResponse, { taskId: number; taskWayPointId: number }>({
          query: GET_HAS_TMS_LOADING_DETAIL,
          variables: { taskId, taskWayPointId },
          fetchPolicy: 'network-only'
        });

        if (!data?.hasTmsLoadingDetail) {
          const message = t('errors.business.tms.taskWaypoint.notLoadedForSealing');
          await Dialog.alert({
            content: (
              <div style={{ backgroundColor: '#ffffff', padding: '16px' }}>
                <div className="text-base text-center text-gray-800">
                  {message}
                </div>
              </div>
            ),
            confirmText: t('pda.common.cancel'),
          });
          return;
        }
      } catch (e) {
        Toast.show({
          content: parseException(e).message,
          position: 'center',
          duration: 2000
        });
        return;
      }
    }

    navigate(PdaPath.PDA_TMS_LOADING_DETAIL, { state: { backRoute, ...taskData } });
  };

  return (
      <div className="w-full h-full flex flex-col">
        <div className="flex flex-col gap-2 p-2 flex-1 overflow-y-auto pb-20">
          <Header title={t('pda.pages.shipOut.loading')} backRoute={backRoute}/>
          <Pda/>
          {/* 步骤节点展示 */}
          <TmsWaypointStepIndicator currentStep={1}/>
          {/*{taskData?.task && <TmsTaskCardItem task={taskData.task} showButtons={false} />}*/}
          <PageBody taskId={taskId} taskWayPointId={taskWayPointId} routeName={routeName}/>
        </div>

          {/* 固定在底部的按钮 - 直接 DOM 操作，避免 React 重新渲染 */}
          <div 
            ref={buttonContainerRef}
            className={`${getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').className} transition-opacity duration-300`}
            style={{
              ...getBottomNavBarContainerStyle('fixed bottom-0 left-0 w-full p-2 bg-white border-t border-gray-200 z-20').style,
              willChange: 'opacity'
            }}>
            <div className="flex gap-2">
              <Button
                  block
                  color="default"
                  onClick={handleBack}
                  style={{flex: 1}}
              >
                {t('pda.common.back') || 'back'}
              </Button>
              <Button
                  block
                  color="primary"
                  onClick={handleNextStep}
                  style={{flex: 1}}
              >
                {t('pda.common.nextStep') || 'nextStep'}
              </Button>
            </div>
          </div>
        </div>
        );
        }
